import { Helmet } from "react-helmet-async";
import { SITE } from "@/lib/seoData";
import { SEARCH_MODE } from "@/lib/searchConfig";

/**
 * Single SEO head component used on every route.
 * - Sets title / description / canonical / OG / Twitter
 * - Injects an array of JSON-LD schema blocks
 */
export default function SeoHead({
  title,
  description,
  path = "/",
  image = SITE.ogImage,
  type = "website",
  noindex = false,
  robots = "",
  schemas = [],
}) {
  const fullTitle = title
    ? (title.toLowerCase().includes(SITE.name.toLowerCase())
        ? title
        : `${title} | ${SITE.name} — Search Finance Across Australia`)
    : `${SITE.name} — Search Finance Across Australia`;
  const url = `${SITE.url}${path}`;
  // Optional Google Search Console verification code. Set via env at build:
  //   REACT_APP_GOOGLE_SITE_VERIFICATION=abcDEF123...
  const gscVerification = (process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || "").trim();

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description || SITE.description} />
      <link rel="canonical" href={url} />
      {(noindex || robots) && <meta name="robots" content={robots || "noindex, nofollow"} />}
      {!noindex && !robots && <meta name="robots" content="index, follow, max-image-preview:large" />}
      {gscVerification && (
        <meta name="google-site-verification" content={gscVerification} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || SITE.description} />
      <meta property="og:url" content={url} />
      {image && <meta property="og:image" content={image} />}
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter */}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || SITE.description} />
      {image && <meta name="twitter:image" content={image} />}

      {/* JSON-LD schemas */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

// ============ Schema generators ============
export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["Organization", "FinancialService", "LocalBusiness"],
  "@id": `${SITE.url}/#organization`,
  name: SITE.name,
  alternateName: SITE.alternateName,
  legalName: SITE.legalName,
  url: `${SITE.url}/`,
  logo: SITE.logo,
  description: SITE.description,
  email: SITE.email,
  telephone: SITE.phone,
  taxID: SITE.abn,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.locality,
    addressRegion: SITE.address.region,
    postalCode: SITE.address.postcode,
    addressCountry: SITE.address.country,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: SITE.geo.lat,
    longitude: SITE.geo.lng,
  },
  areaServed: { "@type": "Country", name: "Australia" },
  openingHours: SITE.hours,
  sameAs: [SITE.social.facebook],
  identifier: [
    { "@type": "PropertyValue", name: "Australian Credit Licence", value: SITE.acl },
    { "@type": "PropertyValue", name: "AFCA Member Number", value: SITE.afca },
    { "@type": "PropertyValue", name: "MFAA Member Number", value: SITE.mfaa },
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE.url}/#website`,
  url: SITE.url,
  name: SITE.name,
  publisher: { "@id": `${SITE.url}/#organization` },
  inLanguage: "en-AU",
  // SearchAction is only exposed when the /search endpoint is live (REACT_APP_SEARCH_MODE=on)
  ...(SEARCH_MODE ? {
    potentialAction: {
      "@type": "SearchAction",
      target: { "@type": "EntryPoint", urlTemplate: `${SITE.url}/search?q={search_term_string}` },
      "query-input": "required name=search_term_string",
    },
  } : {}),
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE.url}${it.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(([q, a]) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const personSchema = ({ name, jobTitle, path, knowsAbout = [], sameAs = [] }) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${SITE.url}${path}#person`,
  name,
  jobTitle,
  url: `${SITE.url}${path}`,
  worksFor: { "@id": `${SITE.url}/#organization` },
  ...(knowsAbout.length ? { knowsAbout } : {}),
  ...(sameAs.length ? { sameAs } : {}),
});

export const serviceSchema = ({ name, description, slug, areaServed = "Australia" }) => ({
  "@context": "https://schema.org",
  "@type": "FinancialProduct",
  name,
  description,
  provider: { "@id": `${SITE.url}/#organization` },
  url: `${SITE.url}/finance/${slug}`,
  areaServed,
  category: "Loan",
});

export const localBusinessSchema = ({ city, region, lat, lng, slug }) => ({
  "@context": "https://schema.org",
  "@type": "FinancialService",
  "@id": `${SITE.url}/locations/${slug}#localbusiness`,
  name: `${SITE.name} — ${city}`,
  parentOrganization: { "@id": `${SITE.url}/#organization` },
  url: `${SITE.url}/locations/${slug}`,
  telephone: SITE.phone,
  email: SITE.email,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.locality,
    addressRegion: SITE.address.region,
    postalCode: SITE.address.postcode,
    addressCountry: SITE.address.country,
  },
  geo: { "@type": "GeoCoordinates", latitude: lat, longitude: lng },
  areaServed: { "@type": "City", name: city, containedInPlace: { "@type": "AdministrativeArea", name: region } },
  openingHours: SITE.hours,
});

